import { useState, useReducer, useEffect } from 'react';
import { Button } from '@pmi/dsm-react';
import { Col, Container, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import HubAccountSelectCard from './HubAccountSelectCard';
import {
  useGetHubAccountListQuery,
  useGetHubInvitationListQuery,
} from './apiSlice';
import {
  HubAccountInviteSelectDto,
  HubAccountSelectResponseDto,
} from './models';
import { acceptUserinvitationFlow, verifyUserFlow } from '../../shared/helpers';
import { reportError } from '../../shared/analytics';
import { setHubPageTitle } from '../hubLayoutSlice';
import { setCurrentAccount } from '../../shared/store/shared/accountSlice';

interface HubAccountSelectState {
  selectedAccountUserId: string;
  selectedAccountId: string;
  selectedInviteId: string;
}

export default function HubAccountSelect() {
  const [autoSelectAccount, setAutoSelectAccount] = useState(false);
  const navigate = useNavigate();
  const {
    data: inviteList,
    isLoading: isInviteListLoading,
    isError: isInviteListError,
  } = useGetHubInvitationListQuery();

  const {
    data: accountList,
    isLoading,
    isError,
    error: accountListError,
  } = useGetHubAccountListQuery();

  const dispatchData = useDispatch();

  useEffect(() => {
    dispatchData(setCurrentAccount(null));
    dispatchData(setHubPageTitle('Account Selection'));
  }, []);

  const selectAccountReducer = (
    state: HubAccountSelectState,
    action: { type: string; payload?: any },
  ) => {
    switch (action.type) {
      case 'setAccountUserIdForAccount':
        return {
          selectedAccountId: accountList.find(
            (x) => x.accountUserId === action.payload,
          ).account.id,
          selectedInviteId: '',
          selectedAccountUserId: action.payload,
        };

      case 'setAccountUserIdForInvite':
        return {
          selectedAccountId: '',
          selectedInviteId: inviteList.find(
            (x) => x.accountUserId === action.payload,
          ).inviteCode,
          selectedAccountUserId: action.payload,
        };

      case 'handleAccountSelectSubmit':
        verifyUserFlow(state.selectedAccountId);
        return state;

      case 'handleInviteSelectSubmit':
        acceptUserinvitationFlow(state.selectedInviteId);
        return state;

      default:
        return state;
    }
  };

  const [
    { selectedAccountUserId, selectedAccountId, selectedInviteId },
    dispatch,
  ] = useReducer(selectAccountReducer, {
    selectedAccountUserId: '',
    selectedAccountId: '',
    selectedInviteId: '',
  });

  useEffect(() => {
    if (
      !isInviteListLoading &&
      !isLoading &&
      accountList?.length === 1 &&
      inviteList?.length === 0
    ) {
      setAutoSelectAccount(true);
      verifyUserFlow(accountList[0].account.id);
    }
  }, [accountList, inviteList, isInviteListLoading, isLoading]);

  const handleCancellation = () => {
    navigate('/');
  };

  const fetchingError = 'There was an error fetching Account list';
  useEffect(() => {
    if (isError || isInviteListError) {
      reportError(fetchingError, 1500);
    }
  }, [isError, isInviteListError]);

  const noAccessError =
    'Your account does not have access to any partner&nbsp;accounts or have any pending&nbsp;invitations.';
  useEffect(() => {
    if (accountList?.length === 0 && inviteList?.length === 0) {
      reportError(noAccessError.replaceAll('&nbsp;', ' '), 1500);
    }
  }, [accountList, inviteList]);

  if (isError || isInviteListError) {
    // Don't show anything if it's 401, let Layout.tsx component handle it.
    const castError: any = accountListError;
    if (castError?.status === 401) return null;

    return (
      <div className="box">
        <h3 className="text-center">{fetchingError}</h3>
      </div>
    );
  }

  if (autoSelectAccount) {
    return (
      <div className="box">
        <h3 className="text-center">Redirecting to your account</h3>
      </div>
    );
  }

  if (accountList?.length === 0 && inviteList?.length === 0) {
    return (
      <div className="box">
        <h3
          className="text-center"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: noAccessError }}
        />
        <h4 className="text-center">
          Please contact your administrator for further assistance.
        </h4>
      </div>
    );
  }

  return (
    <div className="box">
      <h3 className="text-center">
        Which training experience would you like to access?
      </h3>
      <Container>
        {isLoading && (
          <div className="p-5">
            <Spinner className="d-block m-auto" />
          </div>
        )}
        {!isLoading && accountList?.length > 0 && (
          <>
            <Row className="justify-content-center">
              {accountList?.map((acc: HubAccountSelectResponseDto) => (
                <HubAccountSelectCard
                  accountUserId={acc.accountUserId}
                  accountDetails={acc.account}
                  handleClick={(id) =>
                    dispatch({
                      type: 'setAccountUserIdForAccount',
                      payload: id,
                    })
                  }
                  key={acc.accountUserId}
                  selectedAccountUserId={selectedAccountUserId}
                />
              ))}
            </Row>
            <Row>
              <Col className="text-center">
                <Button
                  titleText="Cancel"
                  variant="outline-secondary"
                  onClick={handleCancellation}
                />
                <Button
                  disabled={!selectedAccountId}
                  className="m-2"
                  titleText="Select & Proceed"
                  variant="primary"
                  onClick={() =>
                    dispatch({ type: 'handleAccountSelectSubmit' })
                  }
                />
              </Col>
            </Row>
          </>
        )}
      </Container>

      <Container>
        {isInviteListLoading && (
          <div className="p-5">
            <Spinner className="d-block m-auto" />
          </div>
        )}
        {!isInviteListLoading && inviteList?.length > 0 && (
          <>
            <Row className="justify-content-center pt-4">
              <h3 className="text-center">Pending Invitations</h3>

              {inviteList?.map((inv: HubAccountInviteSelectDto) => (
                <HubAccountSelectCard
                  accountUserId={inv.accountUserId}
                  inviteDetails={inv}
                  accountDetails={inv.account}
                  handleClick={(id) =>
                    dispatch({
                      type: 'setAccountUserIdForInvite',
                      payload: id,
                    })
                  }
                  key={inv.accountUserId}
                  selectedAccountUserId={selectedAccountUserId}
                />
              ))}
            </Row>
            <Row>
              <Col className="text-center">
                <Button
                  titleText="Cancel"
                  variant="outline-secondary"
                  onClick={handleCancellation}
                />
                <Button
                  disabled={!selectedInviteId}
                  className="m-2"
                  titleText="Accept Invitation"
                  variant="primary"
                  onClick={() => dispatch({ type: 'handleInviteSelectSubmit' })}
                />
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
}
